import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useNavigate } from "@tanstack/react-router";
import { Building2, ClipboardList, Clock, FileText, Search, Star } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  type PaletteResultItem,
  type PaletteResultKind,
  usePaletteResults,
} from "@/hooks/use-palette-results";
import { cn } from "@/lib/utils";

// Global Cmd+K / Ctrl+K palette. Result aggregation lives in
// usePaletteResults; this component owns presentation + keyboard nav.

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const { results, sections, hasQuery, isLoading, debounced } = usePaletteResults(query);

  // Reset on close so the next open starts fresh. Don't reset on every render;
  // typing should not lose focus or jump the active index back to 0.
  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  // Snap active index back into range whenever the result set changes — a
  // shrinking list shouldn't leave the cursor pointing past the end.
  useEffect(() => {
    if (activeIndex >= results.length) setActiveIndex(0);
  }, [results.length, activeIndex]);

  function go(item: PaletteResultItem) {
    onOpenChange(false);
    if (item.kind === "claim") {
      navigate({ to: "/claims/$claimNumber", params: { claimNumber: item.id } });
    } else if (item.kind === "policy") {
      navigate({ to: "/policy/$policyNumber", params: { policyNumber: item.id } });
    } else {
      navigate({ to: "/agency/$agentNumber", params: { agentNumber: item.id } });
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // Search results render as a single flat list — straight ↓/↑ wrap-around.
    // With no query the palette shows two side-by-side columns (Favorites,
    // Recent); use column-aware nav so ↓ stays within the visible column and
    // ←/→ jumps between them. Without this, ↓ from the bottom of Favorites
    // visually leaps across the screen to the top of Recent.
    if (e.key === "Enter") {
      e.preventDefault();
      const item = results[activeIndex];
      if (item) go(item);
      return;
    }
    if (hasQuery || !sections) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (results.length === 0 ? 0 : (i + 1) % results.length));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) =>
          results.length === 0 ? 0 : (i - 1 + results.length) % results.length,
        );
      }
      return;
    }
    const inFav = sections.favRows.some((r) => r.index === activeIndex);
    const col = inFav ? sections.favRows : sections.recRows;
    const other = inFav ? sections.recRows : sections.favRows;
    const localRow = col.findIndex((r) => r.index === activeIndex);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (col.length === 0) return;
      const next = col[(localRow + 1) % col.length];
      if (next) setActiveIndex(next.index);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (col.length === 0) return;
      const prev = col[(localRow - 1 + col.length) % col.length];
      if (prev) setActiveIndex(prev.index);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      if (other.length === 0) return;
      const target = other[Math.min(Math.max(localRow, 0), other.length - 1)];
      if (target) setActiveIndex(target.index);
    }
  }

  const showSearchEmpty = hasQuery && !isLoading && results.length === 0;
  const showJumpEmpty = !hasQuery && results.length === 0;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50" />
        <DialogPrimitive.Content
          // Position near the top so it feels like a command bar, not a
          // dead-center modal. Pulls focus onto the input as soon as it opens.
          className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 -translate-x-1/2 fixed top-[15vh] left-1/2 z-50 w-[92vw] max-w-2xl translate-y-0 overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-2xl"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <DialogPrimitive.Title className="sr-only">Search</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Search for a policy, claim, or agency by number or name. With no query, your favorites
            and recents are shown.
          </DialogPrimitive.Description>
          <div className="flex items-center gap-2 border-b px-3 py-2.5">
            <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search policies, claims, or agencies…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              aria-label="Search"
            />
            <span className="hidden rounded border bg-muted px-1.5 py-0.5 font-medium text-[10px] text-muted-foreground sm:inline-block">
              esc
            </span>
          </div>

          <div className="max-h-[60vh] overflow-y-auto py-1">
            {hasQuery && isLoading && results.length === 0 && (
              <p className="px-3 py-6 text-center text-muted-foreground text-sm">Searching…</p>
            )}
            {showSearchEmpty && (
              <p className="px-3 py-6 text-center text-muted-foreground text-sm">
                No matches for "{debounced}".
              </p>
            )}
            {hasQuery &&
              results.map((item, index) => (
                <ResultRow
                  key={`${item.source}-${item.kind}-${item.id}`}
                  item={item}
                  isActive={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => go(item)}
                />
              ))}

            {!hasQuery && showJumpEmpty && (
              <p className="px-3 py-6 text-center text-muted-foreground text-sm">
                Type to search, or pin items with the ★ on any dashboard to see them here.
              </p>
            )}
            {sections && (sections.favRows.length > 0 || sections.recRows.length > 0) && (
              <div className="grid grid-cols-2 divide-x">
                <PaletteColumn
                  icon={Star}
                  label="Favorites"
                  emptyHint="Pin a record with ★ on any dashboard to see it here."
                  rows={sections.favRows}
                  activeIndex={activeIndex}
                  onHover={setActiveIndex}
                  onPick={go}
                />
                <PaletteColumn
                  icon={Clock}
                  label="Recent"
                  emptyHint="Records you open will appear here."
                  rows={sections.recRows}
                  activeIndex={activeIndex}
                  onHover={setActiveIndex}
                  onPick={go}
                />
              </div>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

const KIND_META: Record<PaletteResultKind, { Icon: typeof FileText; label: string }> = {
  policy: { Icon: FileText, label: "Policy" },
  claim: { Icon: ClipboardList, label: "Claim" },
  agency: { Icon: Building2, label: "Agency" },
};

function SectionHeader({ icon: Icon, label }: { icon: typeof FileText; label: string }) {
  return (
    <div className="flex items-center gap-1.5 px-3 pt-2 pb-1 font-medium text-[10px] text-muted-foreground uppercase tracking-wider">
      <Icon className="size-3" aria-hidden="true" />
      {label}
    </div>
  );
}

function PaletteColumn({
  icon,
  label,
  emptyHint,
  rows,
  activeIndex,
  onHover,
  onPick,
}: {
  icon: typeof FileText;
  label: string;
  emptyHint: string;
  rows: { item: PaletteResultItem; index: number }[];
  activeIndex: number;
  onHover: (index: number) => void;
  onPick: (item: PaletteResultItem) => void;
}) {
  return (
    <div className="min-w-0">
      <SectionHeader icon={icon} label={label} />
      {rows.length === 0 ? (
        <p className="px-3 py-2 text-muted-foreground text-xs italic">{emptyHint}</p>
      ) : (
        rows.map(({ item, index }) => (
          <ResultRow
            key={`${item.source}-${item.kind}-${item.id}`}
            item={item}
            isActive={index === activeIndex}
            onMouseEnter={() => onHover(index)}
            onClick={() => onPick(item)}
          />
        ))
      )}
    </div>
  );
}

function ResultRow({
  item,
  isActive,
  onMouseEnter,
  onClick,
}: {
  item: PaletteResultItem;
  isActive: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  const meta = KIND_META[item.kind];
  return (
    <button
      type="button"
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 px-3 py-2 text-left text-sm",
        isActive ? "bg-accent text-accent-foreground" : "hover:bg-accent/60",
      )}
    >
      <meta.Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      <span className="rounded bg-muted px-1.5 py-0.5 font-medium text-[10px] text-muted-foreground uppercase tracking-wider">
        {meta.label}
      </span>
    </button>
  );
}
